/**
 * API Service for Clothing Sets
 * Frontend service for managing custom clothing configurations
 */

import type { ClothingSet, ClothingSetInput, WeatherConditions } from '@shared/types';
import apiClient from './api';

/**
 * Response from the match endpoint
 */
export interface MatchResponse {
  matches: ClothingSet[];
  weather: {
    temperature: number;
    conditions: WeatherConditions;
  };
}

/**
 * Get all clothing sets for the current user
 */
export async function getClothingSets(): Promise<ClothingSet[]> {
  const response = await apiClient.get<ClothingSet[]>('/api/clothing/sets');
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch clothing sets');
  }
  return response.data;
}

/**
 * Get a specific clothing set by ID
 */
export async function getClothingSetById(id: string): Promise<ClothingSet> {
  const response = await apiClient.get<ClothingSet>(`/api/clothing/sets/${id}`);
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch clothing set');
  }
  return response.data;
}

/**
 * Find all matching clothing sets for given weather conditions
 * Returns an array of matching sets (empty if none found)
 */
export async function findMatchingClothingSets(
  temperature: number,
  conditions: { rain: boolean; wind: boolean; sun: boolean }
): Promise<MatchResponse> {
  const params = new URLSearchParams();
  params.append('temperature', temperature.toString());
  params.append('rain', conditions.rain.toString());
  params.append('wind', conditions.wind.toString());
  params.append('sun', conditions.sun.toString());

  const response = await apiClient.get<MatchResponse>(
    `/api/clothing/sets/match?${params.toString()}`
  );

  if (!response.success || !response.data) {
    // Return empty matches if no match found
    return {
      matches: [],
      weather: { temperature, conditions },
    };
  }

  return response.data;
}

/**
 * Find the best matching clothing set for given weather conditions
 * @deprecated Use findMatchingClothingSets for multiple matches
 */
export async function findMatchingClothingSet(
  temperature: number,
  conditions: { rain: boolean; wind: boolean; sun: boolean }
): Promise<ClothingSet | null> {
  const result = await findMatchingClothingSets(temperature, conditions);
  return result.matches.length > 0 ? result.matches[0] : null;
}

/**
 * Create a new clothing set
 */
export async function createClothingSet(input: ClothingSetInput): Promise<ClothingSet> {
  const response = await apiClient.post<ClothingSet>('/api/clothing/sets', input);
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create clothing set');
  }
  return response.data;
}

/**
 * Update an existing clothing set
 */
export async function updateClothingSet(id: string, input: ClothingSetInput): Promise<ClothingSet> {
  const response = await apiClient.put<ClothingSet>(`/api/clothing/sets/${id}`, input);
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update clothing set');
  }
  return response.data;
}

/**
 * Delete a clothing set
 */
export async function deleteClothingSet(id: string): Promise<void> {
  const response = await apiClient.delete(`/api/clothing/sets/${id}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to delete clothing set');
  }
}
